import { usePricingPageState } from "./hooks/usePricingPageState";
import React, { lazy, Suspense, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const NewUserRedirect = ({ children }: { children: React.ReactNode }) => {
  const { isLoading, shouldShowPricingPage } = usePricingPageState();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;
    if (location.pathname === '/pricing') return;
    if (shouldShowPricingPage) navigate("/pricing");
  }, [isLoading, shouldShowPricingPage, navigate, location.pathname]);

  useEffect(() => {
    const justCompleted = sessionStorage.getItem('justCompletedProfile');
    if (justCompleted === 'true') sessionStorage.removeItem('justCompletedProfile');

    const timer = setTimeout(() => {
      const redirectAfterLogin = sessionStorage.getItem('redirectAfterLogin');
      if (redirectAfterLogin && !location.pathname.includes('complete-profile')) {
        sessionStorage.removeItem('redirectAfterLogin');
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return children;
};

import {
  BrowserRouter,
  Routes,
  Route,
} from "react-router-dom";
// Pages route-splittées (React.lazy) : chaque page devient un chunk séparé chargé
// à la demande → le bundle initial n'embarque plus tout le code des pages, ce qui
// réduit fortement le chargement à froid (le chunk monolithique de ~6,7 Mo). (#238)
const PricingPage = lazy(() => import("./pages/Pricing"));
const Index = lazy(() => import("./pages/Index"));
const CaseView = lazy(() => import("./pages/CaseView"));
const FactDetailPage = lazy(() => import("./pages/FactDetailPage"));
const PartyDetailPage = lazy(() => import("./pages/PartyDetailPage"));
const DecisionViewPage = lazy(() => import("./pages/DecisionViewPage"));
const Auth = lazy(() => import("./pages/Auth"));
const Settings = lazy(() => import("./pages/Settings"));
const Personnalisation = lazy(() => import("./pages/Personnalisation"));
const BibliothequeModeles = lazy(() => import("./pages/BibliothequeModeles"));
const CompleteProfilePage = lazy(() => import("./pages/complete-profile"));
import { AuthProvider } from "./components/AuthProvider";
import { MicrosoftAuthProvider } from "./sharepoint-integration";
import ProtectedRoute from "./components/ProtectedRoute";
import BackendHealthGate from "./components/BackendHealthGate";
import DashboardLayout from "./components/DashboardLayout";
import { ToasterLayer } from "@/components/ui/toaster-layer";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
const AgentV3Page = lazy(() => import('./features/hector-agent-casediscovery-v3/pages/AgentV3Page'));
const PiecesToolkitPage = lazy(() => import('./features/pieces-toolkit/pages/PiecesToolkitPage'));
const NotFound = lazy(() => import("./pages/NotFound"));
const MicrosoftCallback = lazy(() => import("./pages/MicrosoftCallback"));
const GoogleAuthCallback = lazy(() => import("./pages/GoogleAuthCallback"));
const InscriptionPage = lazy(() => import("./pages/InscriptionPage"));
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
const HelpCenterPage = lazy(() => import("./pages/HelpCenterPage"));
const HelpArticlePage = lazy(() => import("./pages/HelpArticlePage"));
import { Analytics } from "@vercel/analytics/react";
import { PostHogProvider } from 'posthog-js/react';
import { filterPostHogExceptionNoise } from './lib/posthogExceptionFilter';
import { isTerminalHttpError } from './lib/errors/isTerminalHttpError';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      // Retry up to 3 times with exponential backoff for network/server errors.
      // Auth errors (401/403) are not retried — apiClient redirects to /auth on 401.
      retry: (failureCount, error) => {
        if (isTerminalHttpError(error)) return false;
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000),
    },
  },
});

const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com',
  autocapture: true,
  capture_pageview: false,
  // Error Tracking natif — alimente la vue dédiée PostHog avec stack traces et grouping
  capture_exceptions: {
    capture_unhandled_errors: true,
    capture_unhandled_rejections: true,
    capture_console_errors: false,
  },
  // Session Replay — replay vidéo lié automatiquement aux erreurs
  disable_session_recording: false,
  enable_recording_console_log: true,
  before_send: filterPostHogExceptionNoise,
};

// Fallback plein écran le temps de charger le chunk d'une page lazy hors shell
// (auth, inscription, pricing…). Pour les pages DANS le shell, c'est le Suspense
// interne de DashboardLayout (autour de l'Outlet) qui prend le relais, gardant la
// sidebar montée.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-grey-100">
    <div className="h-5 w-5 animate-spin rounded-full border-2 border-grey-300 border-t-charcoal" />
  </div>
);

function App() {
  const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

  const AppContent = () => (
    <AppErrorBoundary level="root">
      {/* Le SDK Stripe frontend a été retiré (P4, 2026-08-12) : Checkout et
          Portal sont des pages hébergées par Stripe — le backend renvoie une
          `url` et le navigateur y navigue. Aucun <Elements> nécessaire. */}
      <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <MicrosoftAuthProvider>
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/inscription" element={<InscriptionPage />} />
                  {/* Lien cabinet : même page, parcours « essai de 30 jours sans carte ». */}
                  <Route path="/inscription/cabinet" element={<InscriptionPage cabinetTrial />} />
                  {/* Lien partenaire (`/inscription/uja`) : remise négociée + suivi. Slug hors catalogue = inscription standard. */}
                  <Route path="/inscription/:partnerSlug" element={<InscriptionPage />} />
                  <Route path="/complete-profile" element={<CompleteProfilePage />} />
                  <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
                  <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />

                  <Route element={<ProtectedRoute />}>
                    {/* BackendHealthGate : prend le contrôle de l'écran avec
                        <ServerUnavailable/> si le backend est injoignable au
                        chargement (erreur réseau sur le bootstrap /cabinet-members/me). */}
                    <Route element={<BackendHealthGate />}>
                      {/* /pricing reste HORS de la layout route : c'est la seule
                          page protégée qui ne porte pas le chrome DashboardLayout. */}
                      <Route path="/pricing" element={<PricingPage />} />

                      {/* DashboardLayout monté UNE fois en layout route → sidebar +
                          WebSocketPoolProvider + dialogs persistent entre les
                          navigations (l'<Outlet/> seul change). Supprime le remount
                          DOM = source principale du flickering / « rechargement
                          brutal » (#238). La gouttière entre la barre latérale et la
                          fenêtre de page est UNIQUE pour toutes les routes — variable
                          CSS `--content-gutter`, posée dans DashboardLayout. */}
                      <Route element={<DashboardLayout />}>
                        <Route path="/" element={<NewUserRedirect><Index /></NewUserRedirect>} />
                        <Route path="/cases/:id" element={<CaseView />} />
                        <Route path="/cases/:caseId/faits/:slug" element={<FactDetailPage />} />
                        <Route path="/cases/:caseId/parties/:slug" element={<PartyDetailPage />} />
                        <Route path="/assistant" element={<AgentV3Page />} />
                        <Route path="/decisions/:ordre/:id" element={<DecisionViewPage />} />
                        <Route path="/outils" element={<PiecesToolkitPage />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/personnalisation" element={<Personnalisation />} />
                        <Route path="/modeles" element={<BibliothequeModeles />} />
                        <Route path="/aide" element={<HelpCenterPage />} />
                        <Route path="/aide/:articleId" element={<HelpArticlePage />} />
                      </Route>
                    </Route>
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
                <ToasterLayer />
                <Analytics />
              </MicrosoftAuthProvider>
            </AuthProvider>
          </BrowserRouter>
      </QueryClientProvider>
    </AppErrorBoundary>
  );

  if (posthogKey) {
    return (
      <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
        <AppContent />
      </PostHogProvider>
    );
  }

  return <AppContent />;
}

export default App;
